import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  IonCol,
  IonGrid,
  IonIcon,
  IonLabel,
  IonRow,
  IonItemDivider,
  IonList,
  IonButton,
  IonItem,
  IonToggle,
} from '@ionic/react'
import { copyOutline, stopwatchOutline } from 'ionicons/icons'
import { VitalsConstants } from '../store/vitals.constants'
import { IRootState } from '../../../store'
import { useTranslation } from 'react-i18next'
import '../page/Vitals.page.css'
import './DetailsSection.component.css'
import {
  setLastVitals,
  saveVitals,
  setAssessedBy,
  setDate,
  setTime,
  showAlert,
  setPTA,
  syncDateTime,
  syncPta,
} from '../store/vitals.actions'
import NumberPad from '../../../components/numberPad/NumberPad.component'
import { transformDate, transformTime } from '../../../utilities'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import Validation from '../../../components/validation/Validation.component'
import { getVitalOptionsSelector } from '../../options/selectors'
import { IOption } from '../../../common/common.types'
import { getServiceConfigSelector } from '../../configurations/selectors/serviceConfig.selector'
import { getPatientTimelineSelector } from '../../timeline/selectors/patientTimeline.selector'
import {
  appendSeconds,
  isValidZollNowDate,
  isValidZollTime,
  truncateSecondsIfNecessary,
} from '../../../utilities/dateTime.util'
import { getTimesSelector } from '../../times/selectors/getTimes.selector'
import { getPTADateTimeErrorMessage, isProcDateTimePTA } from '../../../utilities/procedures.util'
import { clearValidationErrorsByPathList } from '../../../utilities/runRulesValidation.util'
import { IRulesState } from '../../rules/store/rules.types'
import { getRulesSelector } from '../../rules/selectors'

const DetailsSection: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const rules: IRulesState = useSelector(getRulesSelector)
  const vitals = useSelector((state: IRootState) => state.vitals)
  const { pta, date, time, assessedBy, key, showRepeatVitals, reRunValidations } = vitals
  const vitalOptions = useSelector(getVitalOptionsSelector)
  const { assessedByOptions } = vitalOptions
  const serviceConfig = useSelector(getServiceConfigSelector)
  const { SHOW_REPEAT_VITALS, DISPLAY_SECONDS } = serviceConfig
  const { filteredItems } = useSelector(getPatientTimelineSelector)
  const { atPatientDate, atPatientTime, onSceneDate, onSceneTime } = useSelector(getTimesSelector)
  const displaySeconds = !!DISPLAY_SECONDS

  const useLastVitals = () => {
    const timelineVitals = filteredItems?.filter((item) => item.itemType === 'Vitals' && item.key !== key)
    if (timelineVitals && timelineVitals.length > 0) {
      const time = timelineVitals[timelineVitals.length - 1].labelHeader
      const date = timelineVitals[timelineVitals.length - 1].subHeader
      dispatch(
        showAlert({
          header: '',
          message: `Using vitals recorded on ${time} ${date}`,
          buttons: [
            {
              text: t('common.buttons.Cancel'),
            },
            {
              text: t('common.buttons.Ok'),
              handler: () => {
                dispatch(setLastVitals())
              },
            },
          ],
        })
      )
    }
  }

  const assessedByOnChange = (option: IOption) => {
    dispatch(setAssessedBy(option))
    dispatch(saveVitals())
  }

  const runPTAValidation = () => {
    const errMsg = getPTADateTimeErrorMessage(
      atPatientDate,
      atPatientTime,
      onSceneDate,
      onSceneTime,
      date,
      time,
      displaySeconds
    )
    if (errMsg) {
      dispatch(showAlert({ message: errMsg }))
      dispatch(setPTA(false))
      dispatch(saveVitals())
      dispatch(syncPta())
    }
  }

  useEffect(() => {
    if (pta && isValidZollNowDate(date)) {
      runPTAValidation()
    } else if (!pta && isValidZollNowDate(date) && isValidZollTime(time)) {
      if (isProcDateTimePTA(atPatientDate, atPatientTime, onSceneDate, onSceneTime, date, time, displaySeconds)) {
        dispatch(setPTA(true))
        dispatch(saveVitals())
        dispatch(syncPta())
      }
    }
  }, [date, time, pta])

  const setTimeOnChange = (numbers: string[], oldTime?: string) => {
    const oldTimeParts = oldTime?.split('') || []
    if (oldTimeParts.includes(':') && !numbers.includes(':')) {
      numbers.pop()
    }
    const newTime = transformTime(numbers.join(''), displaySeconds)

    dispatch(setTime(newTime))
    dispatch(syncDateTime())
    dispatch(saveVitals())
  }

  const onBlur = () => {
    const newTime = appendSeconds(time, displaySeconds)
    dispatch(setTime(newTime))
    dispatch(syncDateTime())
    dispatch(saveVitals())
  }

  const clearValidations = () => {
    rules.errors = clearValidationErrorsByPathList(rules.errors, [VitalsConstants.SET_DATE, VitalsConstants.SET_TIME])
  }

  const setCurrentTimeOnChange = () => {
    dispatch({ type: VitalsConstants.ADD_CURRENT_TIME })
    dispatch(syncDateTime())
    dispatch(saveVitals())
    clearValidations()
  }

  const dateOnChange = (numbers: string[], oldDate?: string) => {
    const oldDateParts = oldDate?.split('') || []
    const oldFwdSlashCount = oldDateParts.filter((o: string) => o === '/').length
    const newFwdSlashCount = numbers.filter((n: string) => n === '/').length
    if (newFwdSlashCount < oldFwdSlashCount) {
      numbers.pop()
    }
    const newDate = transformDate(numbers.join(''))
    dispatch(setDate(newDate))
    dispatch(syncDateTime())
    dispatch(saveVitals())
  }

  const checkAssessedBy = useMemo(() => {
    if (assessedBy) {
      const assessedByIndex = assessedByOptions.findIndex((option) => option.id === assessedBy.id)
      if (assessedByIndex === -1) {
        return true
      }
      return false
    }
  }, [assessedBy, assessedByOptions])

  const isShowRepeatVitals = () => {
    return (
      SHOW_REPEAT_VITALS === 1 &&
      filteredItems &&
      filteredItems?.filter((item) => item.itemType === 'Vitals' && item.key !== key).length > 0 &&
      showRepeatVitals
    )
  }

  const onPTAClick = () => {
    const isPTA = !pta
    dispatch(setPTA(isPTA))
    dispatch(saveVitals())
    dispatch(syncPta())
  }

  useEffect(() => {
    time && dispatch(setTime(time))
    date && dispatch(setDate(date))
  }, [reRunValidations])

  return (
    <>
      <IonList id='DetailsSection'>
        <IonItemDivider color='light' className='ion-padding'>
          <IonLabel className='grid-item-divider-label' color='medium'>
            {t('vitals.header')}
          </IonLabel>
        </IonItemDivider>
        <IonGrid className='ion-no-padding'>
          <IonRow data-testid='TimesRow' className='ion-padding-horizontal'>
            <IonCol className='picklist'>
              <IonLabel>{t('vitals.details.form.Assessment')}</IonLabel>
            </IonCol>
          </IonRow>
          <IonRow
            id='TimesRow'
            data-testid='TimesRow'
            className='mobile-nowrap ion-padding-horizontal times-row-container'>
            <IonCol className='picklist' data-testid='Column'>
              <NumberPad
                dataTestId='Time'
                className='times-button'
                placeholder={displaySeconds ? 'HH:MM:SS' : 'HH:MM'}
                values={truncateSecondsIfNecessary(time, displaySeconds).split('')}
                onNumPadClick={setTimeOnChange}
                onBlur={onBlur}
              />
            </IonCol>

            <IonCol className='picklist ion-text-center'>
              <IonButton
                data-testid='SetCurrentTime'
                className='transform-none set-time-button'
                fill='clear'
                color='primary-alt'
                onClick={setCurrentTimeOnChange}>
                <IonIcon size='large' icon={stopwatchOutline} />
                <IonLabel className='ion-hide-md-down'>{t('vitals.details.form.AddCurrentTime')}</IonLabel>
              </IonButton>
            </IonCol>

            <IonCol className='picklist times-row-end-button' data-testid='Column'>
              <NumberPad
                dataTestId='Date'
                className='times-button'
                placeholder='MM/DD/YY'
                values={date.split('')}
                onNumPadClick={dateOnChange}
              />
            </IonCol>
            <IonCol className='ion-hide-md-down' />
          </IonRow>
          <IonRow>
            <Validation id={VitalsConstants.SET_TIME} cardinalityKey={key} showReduxStateErrorsOnlyFlag={true} />
            <Validation id={VitalsConstants.SET_DATE} cardinalityKey={key} showReduxStateErrorsOnlyFlag={true} />
          </IonRow>

          <hr className='field-divider' />

          <IonRow class='ion-align-items-baseline'>
            <IonCol className='picklist'>
              <SingleSelect
                isPHI
                dataTestID='AssessedBy'
                options={assessedByOptions}
                selectedOption={assessedBy}
                listHeader={t('vitals.details.form.AssessedBy')}
                label={t('vitals.details.form.AssessedBy')}
                action={assessedByOnChange}
              />
              {checkAssessedBy && <IonLabel color='danger'>{t('vitals.details.form.AssessedByError')}</IonLabel>}
            </IonCol>
          </IonRow>

          <hr className='field-divider' />
        </IonGrid>
        {isShowRepeatVitals() && (
          <IonRow className='ion-float-right ion-padding-right'>
            <IonButton data-testid='UseLastVitals' fill='clear' color='primary-alt' onClick={useLastVitals}>
              <IonIcon icon={copyOutline} />
              <IonLabel>{t('vitals.useLastVitals')}</IonLabel>
            </IonButton>
          </IonRow>
        )}

        <IonItemDivider color='light' />

        <IonRow>
          <IonCol className='picklist'>
            <IonItem lines='none' className='toggle-item'>
              <IonLabel>{t('procedure.header.pta')}</IonLabel>
              <IonToggle slot='start' name='pta' checked={pta} onClick={onPTAClick} color='zoll-blue-secondary' />
            </IonItem>
          </IonCol>
        </IonRow>
      </IonList>
    </>
  )
}

export default DetailsSection
